import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../lib/axiosInstance';
import UploadImages from '../components/UploadImages';
import VariationsComponent from '../components/VariationsComponent';
import Pricing from '../components/Pricing';
import CategoryProduct from '../components/CategoryProduct';
import InputUi from '../components/InputUi';
import RichTextUi from '../components/RichTextUi';
import { listSizeCharts } from '../lib/api/sizeChartApi';

const Container = ({ label, children, gap = 3, containerclass = '' }) => (
    <div className={`card p-6 ${containerclass}`}>
        {label && <h3 className="text-lg font-semibold mb-4 text-[var(--text)]">{label}</h3>}
        <div className={`flex flex-col gap-${gap}`}>
            {children}
        </div>
    </div>
);

function EditProduct() {
    const { id } = useParams();
    const navigate = useNavigate();
    const uploadRef = useRef();
    const galleryRef = useRef();

    const [product, setProduct] = useState(null);
    // Refs used by child components to avoid parent-child update loops
    const currentAttributesRef = useRef([]);
    const currentVariationsRef = useRef([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [rawResponse, setRawResponse] = useState(null);
    const [sizeCharts, setSizeCharts] = useState([]);

    // Fetch size charts
    useEffect(() => {
        const fetchSizeCharts = async () => {
            try {
                const res = await listSizeCharts();
                if (res.success) {
                    setSizeCharts(res.data || []);
                }
            } catch (err) {
                console.error('Error fetching size charts:', err);
            }
        };
        fetchSizeCharts();
    }, []);

    const fetchProduct = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axiosInstance.get(`/brand/details/${id}`);
            setRawResponse(data);
            console.debug('EditProduct: backend response for /brand/details/:id', data);
            if (data.success) {
                // Ensure defaults for editor
                const p = data.data || {};
                setProduct({
                    ...p,
                    type: p.type || 'variable',
                    productVariations: p.variations || p.productVariations || [],
                    attributes: p.productAttributes || p.attributes || [],
                    featuredImage: p.featuredImage || [],
                    galleryImage: p.galleryImage || []
                });
            } else {
                setError('Product not found');
            }
        } catch (err) {
            console.error('EditProduct: failed to load product', err?.response?.data || err);
            setError(err.response?.data?.message || 'Failed to load product');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) fetchProduct();
    }, [id, fetchProduct]);

    const updateField = (e, name) => {
        const value = e?.target ? e.target.value : e;
        setProduct(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            // If new images uploaded, use them; otherwise keep existing
            let featured = product.featuredImage;
            let gallery = product.galleryImage;

            if (uploadRef.current?.uploadImageFunction) {
                const uploadedFeatured = await uploadRef.current.uploadImageFunction();
                if (uploadedFeatured && Array.isArray(uploadedFeatured) && uploadedFeatured.length > 0) {
                    featured = uploadedFeatured;
                }
            }

            if (galleryRef.current?.uploadImageFunction) {
                const uploadedGallery = await galleryRef.current.uploadImageFunction();
                if (uploadedGallery && Array.isArray(uploadedGallery) && uploadedGallery.length > 0) {
                    gallery = uploadedGallery;
                }
            }

            const payload = {
                ...product,
                productID: product.productID || id,
                featuredImage: featured,
                galleryImage: gallery,
                productVariations: product.productVariations || [],
                attributes: product.attributes || []
            };
            console.log(payload);


            const { data } = await axiosInstance.post('/brand/edit-product', payload);
            if (data.success) {
                alert('Product updated');
                navigate(`/products/${product.productID || id}`);
            } else {
                alert('Failed to update product');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save product');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-[var(--text)]">Loading product...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-center">
                    <div className="text-red-600 mb-4">{error}</div>
                    <button onClick={() => navigate('/products')} className="text-blue-600 hover:text-blue-800">← Back to Products</button>
                </div>
            </div>
        );
    }

    if (!product) return null;

    return (
        <div className="min-h-screen bg-[var(--bg)] p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/products')} className="text-blue-600 hover:text-blue-800">← Back to Products</button>
                        <h1 className="lux-heading text-2xl">Edit Product</h1>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-[var(--accent)] text-[var(--accent-contrast)] rounded-lg hover:opacity-90 disabled:opacity-60"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>

                <div className="grid grid-cols-6 gap-2">
                    <div className="col-span-4 gap-2">
                        <div className="flex flex-col gap-2">
                            <Container gap={3} label={'Basic Information'}>
                                <InputUi label={'Product Title'} value={product.name || ''} datafunction={(e) => updateField(e, 'name')} />
                                <RichTextUi label={'Product Description'} value={product.description || ''} onChange={(val) => updateField({ target: { value: val } }, 'description')} />
                                <div className="grid grid-cols-3 gap-2">
                                    <RichTextUi label={'Tab 1 - Material Care'} value={product.tab1 || ''} onChange={(val) => updateField({ target: { value: val } }, 'tab1')} />
                                    <RichTextUi label={'Tab 2 - Styling and Suggestion'} value={product.tab2 || ''} onChange={(val) => updateField({ target: { value: val } }, 'tab2')} />
                                    <RichTextUi label={'Tab 3 - Product Specifications'} value={product.tab3 || ''} onChange={(val) => updateField({ target: { value: val } }, 'tab3')} />
                                </div>
                            </Container>
                            <Container gap={3} label={'Pricing & Discount'}>
                                <Pricing setProducts={setProduct} products={product} />
                            </Container>
                            <Container gap={3} label={'Variation & Stocking'}>
                                <VariationsComponent
                                    setProducts={Object.assign(setProduct, { currentAttributesRef, currentVariationsRef })}
                                    defaultValue={product.attributes}
                                    defaultVariation={product.productVariations}
                                />
                            </Container>

                            <Container gap={3} label={'Size Chart (Optional)'}>
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-medium text-[var(--muted)]">Select Size Chart</label>
                                    <select
                                        className="w-full px-3 py-2 border border-[var(--border)] rounded-md bg-[var(--surface)] text-[var(--text)] text-sm"
                                        value={product.sizeChartUrl || ''}
                                        onChange={(e) => setProduct(prev => ({ ...prev, sizeChartUrl: e.target.value || null }))}
                                    >
                                        <option value="">None</option>
                                        {sizeCharts.map(chart => (
                                            <option key={chart.id} value={chart.imgUrl}>
                                                {chart.chartName}
                                            </option>
                                        ))}
                                    </select>
                                    {product.sizeChartUrl && (
                                        <div className="mt-2">
                                            <p className="text-xs text-[var(--muted)] mb-1">Preview:</p>
                                            <div className="w-40 h-40 border border-[var(--border)] rounded overflow-hidden bg-[var(--bg)]">
                                                <img
                                                    src={product.sizeChartUrl}
                                                    alt="Size chart preview"
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Container>
                        </div>
                    </div>
                    <div className="col-span-2">
                        <div className="flex flex-col gap-2">

                            <Container containerclass={'bg-[var(--surface)]'}>
                                <div className="overflow-x-auto">
                                    <pre className="col-span-2 mt-4 p-2 text-[var(--text)] rounded text-xs whitespace-pre max-w-full">
                                        {JSON.stringify(product, null, 2)}
                                    </pre>
                                </div>
                            </Container>
                            <Container label={'Categories'}>
                                <CategoryProduct
                                    setProducts={setProduct}
                                    products={product}
                                    isEditable={true}
                                    oldValue={product.categories || []}
                                />
                            </Container>
                            <Container gap={3} label={'Featured Images (re-upload to replace)'}>
                                <UploadImages
                                    ref={uploadRef}
                                    maxImages={2}
                                    defaultImages={product.featuredImage}
                                    setProducts={setProduct}
                                    products={product}
                                />
                            </Container>
                            <Container gap={3} label={'Gallery Images (re-upload to replace)'}>
                                <UploadImages
                                    ref={galleryRef}
                                    maxImages={8}
                                    defaultImages={product.galleryImage}
                                    setProducts={setProduct}
                                    products={product}
                                />
                            </Container>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditProduct;


